/**
 * Der zentrale Uebersetzer "Fehlerobjekt -> deutscher Klartext" (Fehleranzeige Paket 1, Spec
 * `docs/superpowers/specs/2026-10-19-fehleranzeige-fundament-design.md`, Abschnitt 2).
 *
 * `fehlertext()` ist die EINZIGE Stelle, die aus einem Fehler einen Nutzertext macht. Ohne sie
 * gaebe es je Aufrufstelle eine eigene Wahrheit (`fehler.message` zeigt "HTTP 413" oder "Failed to
 * fetch", ein `catch {}` zeigt gar nichts). `null` heisst: nicht melden (Abbruch, 401).
 *
 * Die Reihenfolge der Pruefungen ist Teil des Vertrags:
 * 1. `TimeoutError` VOR dem Abbruch - `AbortSignal.timeout()` setzt danach auch `signal.aborted`.
 * 2. Chunk-Ladefehler VOR dem Netzfehler-Muster - Chrome meldet "Failed to fetch dynamically
 *    imported module", und das beginnt wie ein Netzfehler. Gemeint ist aber: die Anwendung wurde
 *    ausgerollt, die alte Seite kennt ihre Teile nicht mehr.
 * 3. `SitzungsFehler` VOR `ApiFehler` - er ist eine Unterklasse davon.
 */
import {
  AntwortUnvollstaendigFehler,
  ApiFehler,
  nameVon,
  servertextAus,
  SitzungsFehler,
  VerbindungsFehler,
} from "../api/basis.ts";

/**
 * Clientcode, der einen Satz fuer den Nutzer wirft (`throw new NutzerFehler("Position 3 hat keine
 * gueltige Menge.")`). `fehlertext()` gibt dessen `message` unveraendert aus. Ein gewoehnlicher
 * `Error` gilt dagegen als Programmfehler und ergibt den Sammeltext `FEHLERTEXTE.unerwartet` - ohne
 * diese Klasse gingen die wichtigsten Warnungen darin unter.
 */
export class NutzerFehler extends Error {
  constructor(meldung: string) {
    super(meldung);
    this.name = "NutzerFehler";
  }
}

export const FEHLERTEXTE = {
  verbindung: "Keine Verbindung zum Server. Bitte Netzwerk prüfen und erneut versuchen.",
  zeitueberschreitung:
    "Der Server hat nicht rechtzeitig geantwortet. Ob die Änderung angekommen ist, ist unklar – bitte neu laden und prüfen.",
  // Bewusst ohne Zahl: welches Limit der Proxy setzt, regelt die Upload-Session, und eine falsche
  // Zahl waere schlimmer als keine.
  zuGross: "Die Datei ist zu groß – der Server hat sie abgelehnt.",
  serverAntwortetNicht: "Der Server antwortet gerade nicht. Bitte gleich noch einmal versuchen.",
  serverfehler:
    "Auf dem Server ist ein Fehler aufgetreten. Bitte erneut versuchen; tritt er wieder auf, bitte Bescheid geben.",
  keineBerechtigung: "Dafür fehlt die Berechtigung.",
  // Ein 2xx, dessen Koerper abriss (`AntwortUnvollstaendigFehler`). Beim Lesen genuegt "neu laden"; bei jeder anderen
  // Methode kann das Schreiben gelungen sein - deshalb der Hinweis auf den unklaren Ausgang und KEIN Rat zum
  // Wiederholen (`lib/ausgangUnklar.ts`: der Schreibtext zaehlt dort zum unklaren Ausgang, keiner der beiden zu den
  // Wiederholungsraeten).
  antwortUnvollstaendigLesen: "Die Antwort des Servers kam unvollständig an. Bitte neu laden.",
  antwortUnvollstaendigSchreiben:
    "Die Antwort des Servers kam unvollständig an. Ob die Änderung angekommen ist, ist unklar – bitte neu laden und prüfen.",
  neuLaden: "Die Anwendung wurde aktualisiert. Bitte die Seite neu laden (F5).",
  unerwartet:
    "Unerwarteter Fehler im Browser. Bitte die Seite neu laden; tritt er wieder auf, bitte Bescheid geben.",
  abgelehnt(status: number): string {
    return `Der Server hat die Anfrage abgelehnt (Status ${status}).`;
  },
} as const;

/** Das generische `error` der Fehler-Middleware des Backends (`backend/src/app.ts`, 500). */
const BACKEND_GENERIKUM = "Serverfehler";

/**
 * Chrome, Firefox und Safari melden ein gescheitertes `import()` mit je eigenem Wortlaut; Vite wirft
 * vorher `Unable to preload CSS for …`, wenn die CSS eines Lazy-Chunks nicht nachladbar ist.
 */
const CHUNK_MUSTER =
  /dynamically imported module|importing a module script failed|ChunkLoadError|unable to preload css/i;

/**
 * Die `TypeError`-Texte von `fetch` bei Netzfehlern: Chrome, Firefox, Safari, Node/undici
 * (`fetch failed`) und whatwg-fetch (`network request failed`).
 */
const NETZFEHLER_MUSTER = /failed to fetch|networkerror|load failed|fetch failed|network request failed/i;

function textVon(fehler: unknown): string {
  if (typeof fehler !== "object" || fehler === null) return "";
  const meldung = (fehler as { message?: unknown }).message;
  return typeof meldung === "string" ? meldung : "";
}

/**
 * Ein gescheitertes `import()` eines Lazy-Chunks (ein alter Tab nach einem Deployment). REIN: schreibt nie in die
 * Konsole, damit auch `FehlerGrenze.getDerivedStateFromError` (Renderphase) es fragen darf; `fehlertext()` meldet einen
 * Programmfehler dagegen mit `console.error`.
 */
export function istChunkLadefehler(fehler: unknown): boolean {
  return nameVon(fehler) === "ChunkLoadError" || CHUNK_MUSTER.test(textVon(fehler));
}

function apiUrsache(fehler: ApiFehler): string {
  const servertext = servertextAus(fehler.koerper);
  if (servertext !== null) {
    return servertext === BACKEND_GENERIKUM ? FEHLERTEXTE.serverfehler : servertext;
  }
  // Kein JSON (Proxy-HTML, leerer Koerper): nur der Status sagt etwas.
  if (fehler.status === 413) return FEHLERTEXTE.zuGross;
  if (fehler.status === 403) return FEHLERTEXTE.keineBerechtigung;
  if (fehler.status >= 500) return FEHLERTEXTE.serverAntwortetNicht;
  return FEHLERTEXTE.abgelehnt(fehler.status);
}

function ursachesatz(fehler: unknown): string | null {
  const name = nameVon(fehler);
  if (name === "TimeoutError") return FEHLERTEXTE.zeitueberschreitung;
  if (name === "AbortError" || fehler instanceof SitzungsFehler) return null;
  // Ein leerer Satz ergaebe eine leere Meldung; er faellt durch bis zum Sammeltext unten.
  if (typeof fehler === "string" && fehler.trim() !== "") return fehler;
  if (fehler instanceof NutzerFehler && fehler.message.trim() !== "") return fehler.message;
  if (fehler instanceof VerbindungsFehler) return FEHLERTEXTE.verbindung;
  if (fehler instanceof AntwortUnvollstaendigFehler) {
    return fehler.methode === "GET"
      ? FEHLERTEXTE.antwortUnvollstaendigLesen
      : FEHLERTEXTE.antwortUnvollstaendigSchreiben;
  }
  if (istChunkLadefehler(fehler)) return FEHLERTEXTE.neuLaden;
  // Ein nackter Netzfehler: Aufrufer, die `fetch` nicht ueber `anfrage()` rufen.
  if (name === "TypeError" && NETZFEHLER_MUSTER.test(textVon(fehler))) return FEHLERTEXTE.verbindung;
  if (fehler instanceof ApiFehler) return apiUrsache(fehler);
  // Programmfehler: der Nutzer bekommt den Sammeltext, der Entwickler das Original.
  console.error("Unerwarteter Fehler:", fehler);
  return FEHLERTEXTE.unerwartet;
}

/**
 * Der Nutzertext zu einem Fehler. `null` heisst "nicht melden" (Abbruch, 401: die Anmeldemaske
 * uebernimmt). Mit `kontext` kommt `"<kontext> <Ursache>"`, etwa "Die Position konnte nicht
 * gespeichert werden. Keine Verbindung zum Server. …"; ohne `kontext` der Ursachesatz allein, damit
 * man ihn in einen eigenen Satz einbetten kann.
 */
export function fehlertext(fehler: unknown, kontext?: string): string | null {
  const ursache = ursachesatz(fehler);
  if (ursache === null) return null;
  return kontext ? `${kontext} ${ursache}` : ursache;
}
